import { useCallback, useRef, useState } from "react";
import PageWrapper from "../page-wrapper/page-wrapper.components";
import { DocumentCard, FamilyCard } from "./document-card";
import { GoalPicker, ProductFilter } from "./document-filters";
import { DOCUMENTS, MEET_GARY, SECTIONS } from "./marketing-data";
import { countLine, goalDocuments, productTabs, resolveTab } from "./marketing-filters";
import { buildStoryCatalog } from "./meet-gary-catalog";
import MeetGaryPanel from "./meet-gary-panel";
import { MoreBelow } from "./more-below";

const LIVE_CATALOG = buildStoryCatalog(MEET_GARY);

export const MarketingDocuments = ({ catalog = LIVE_CATALOG, documents = DOCUMENTS }) => {
    const [panelOpen, setPanelOpen] = useState(false);
    const [goal, setGoal] = useState("all");
    const [tab, setTab] = useState("all");
    const rootRef = useRef(null);
    const openPanel = useCallback(() => setPanelOpen(true), []);
    const closePanel = useCallback(() => setPanelOpen(false), []);

    const pool = documents.filter((d) => !d.family || catalog.storyCount > 0);
    const goalDocs = goalDocuments(pool, goal);
    const tabs = productTabs(SECTIONS, goalDocs);
    const visible = tab === "all" ? goalDocs : goalDocs.filter((d) => d.section === tab);

    const filtering = goal !== "all" || tab !== "all";
    const sectionLabel = tab === "all" ? undefined : SECTIONS.find((s) => s.id === tab)?.label;

    const pickGoal = (next) => {
        setGoal(next);
        setTab(resolveTab(tab, productTabs(SECTIONS, goalDocuments(pool, next))));
    };

    const clearFilters = () => {
        setGoal("all");
        setTab("all");
    };

    return (
        <div
            ref={rootRef}
            className={`flex-1 font-marketing [&_*:not(.font-display)]:font-marketing pl-4 pr-4 sm:pl-6 sm:pr-6 md:pl-12 md:pr-12 lg:pr-24 py-6 ${panelOpen ? "xl:pr-[448px]" : ""}`}
        >
            <header className="mb-[20px]">
                <p className="text-xs font-bold tracking-[0.12em] text-primary-darker">MARKETING</p>
                <h1 className="mt-1 font-display text-2xl leading-tight text-gray-900 sm:text-3xl">Marketing materials</h1>
                <p className="mt-1.5 text-base text-gray-600">
                    Client-ready documents you can download. Pick a goal or browse by product.
                </p>
            </header>

            <GoalPicker value={goal} onChange={pickGoal} compact={panelOpen} />
            <ProductFilter tabs={tabs} value={tab} onChange={setTab} />
            <div className="mb-[16px] mt-2 flex flex-wrap items-center gap-x-4 text-sm text-gray-600">
                <p>{countLine(visible.length, goal, sectionLabel)}</p>
                {filtering && visible.length > 0 && (
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="min-h-[44px] font-semibold text-primary-darker underline-offset-2 hover:underline"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {visible.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white pl-6 pr-6 py-10 text-center">
                    <p className="text-base font-semibold text-gray-900">Nothing matches that yet.</p>
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-[16px] min-h-[44px] rounded-lg border border-primary pl-4 pr-4 text-sm font-semibold text-primary-darker hover:bg-primary/10"
                    >
                        Clear filters
                    </button>
                </div>
            )}

            {SECTIONS.map((section) => {
                const sectionDocuments = visible.filter((d) => d.section === section.id);
                if (!sectionDocuments.length) return null;
                return (
                    <section key={section.id} aria-labelledby={`section-${section.id}`} className="mb-8">
                        <h2 id={`section-${section.id}`} className="mb-3 text-base font-bold text-gray-900">
                            {section.label}
                        </h2>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                            {sectionDocuments.map((document) =>
                                document.family ? (
                                    <FamilyCard
                                        key={document.id}
                                        document={document}
                                        section={section}
                                        catalog={catalog}
                                        onOpen={openPanel}
                                    />
                                ) : (
                                    <DocumentCard key={document.id} document={document} section={section} />
                                )
                            )}
                        </div>
                    </section>
                );
            })}

            {panelOpen && <MeetGaryPanel catalog={catalog} onClose={closePanel} />}
            <MoreBelow rootRef={rootRef} watch={`${visible.map((d) => d.id).join()}|${panelOpen}`} disabled={panelOpen} />
        </div>
    );
};

const MarketingPage = () => (
    <PageWrapper>
        <MarketingDocuments />
    </PageWrapper>
);

export default MarketingPage;
